import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Loader, Message, Button} from 'semantic-ui-react'
import { GetPaymentProviderURL } from '../../api/services/pms';
import { useLoader } from '../shared/utils';
import { SaveTokenFromIFrameResponse } from '../../api/services/finance';
import PropTypes from "prop-types";
import { onError } from '../shared/messages';
import { ECompanyPayment } from './paymentCommon';
import { useTextResources } from '../../language';

// Parameters: entities the entities that the token will be saved for in form of: [ { type: xxx, id: xxx }, ...]
export const CreateTokenDialog = forwardRef(({ HotelId, entities, folioId, guestId, ccProvider, onTokenAdded }, ref) => {
    const text = useTextResources();
    const [iframeSrc, setIframeSrc] = useState();
    const [message, setMessage] = useState();
    const [providerType, setProviderType] = useState(null); // 'CREDITGUARD' or 'BRIDGEPAY'
    const loader = useLoader();
    const iframeRef = useRef();

    const setNewIframeUrl = async () => {
        if (ccProvider === ECompanyPayment.BRIDGEPAY && !guestId) {
            return;
        }
        const url = await GetPaymentProviderURL({ HotelID: HotelId, GuestId: guestId });
        
        // Detect provider type from URL
        if (url.includes('creditguard')) {
            setProviderType('CREDITGUARD');
        } else if (url.includes('bridgepay')) {
            setProviderType('BRIDGEPAY');
        }
        
        setIframeSrc(url);
    }

    const saveToken = async (result, parameters = {}) => {
        
        try {
            var data = result.UserData6.split(",")
            const ent = parameters?.entities || entities;
            const token = await SaveTokenFromIFrameResponse({
                HotelID: HotelId,
                FolioId: parameters?.folioId || folioId,
                TransactionID: result.TxId,
                EntityGroups: ent ? ent.map(entity => ({
                    EntityID: entity.id,
                    EntityTypeID: entity.type
                })) : null,
                ApplicationID: data[1]
            })
            
            const entity = token.PaymentTokenEntities.find(element => element.EntityTypeID !== 1)
            const tokenResult = {
                ...token,
                EntityID: entity && entity.EntityID,
                EntityTypeID: entity && entity.EntityTypeID,
                CompanyPaymentName: "CREDITGUARD"
            }
            
            await setNewIframeUrl();
            return tokenResult
        } catch (error) {
            onError(error)
            throw error
        } finally {
            loader(false);
        }
    }

    //BridgePay
    const saveIframeToken = async (result, parameters = {}) => {        
        try {
            const ent = parameters.entities || entities;

            const token = await SaveTokenFromIFrameResponse({
                HotelID: HotelId,
                FolioId: parameters.folioId || folioId,
                TransactionID: result.token,
                Cvv: result.encryptedCvv,
                OrderId: result.orderId,
                Email: result.email,
                EntityGroups: ent ? ent.map(entity => ({
                    EntityID: entity.id,
                    EntityTypeID: entity.type
                })) : null,
                CompanyPayment: result.companyPayment
            });
            
            const entity = token.PaymentTokenEntities.find(element => element.EntityTypeID !== 1);
            const tokenResult = {
                ...token,
                EntityID: entity && entity.EntityID,
                EntityTypeID: entity && entity.EntityTypeID,
                CompanyPaymentName: ECompanyPayment[result.companyPayment],
                Code: result.encryptedCvv
            }
            
            await setNewIframeUrl();
            return tokenResult
        } catch (error) {
            onError(error)
            throw error
        } finally {
            loader(false);
        }
    }

    useEffect(() => {
        if (HotelId) {
            (async () => {
                try {
                    loader(true)
                    await setNewIframeUrl();
                } catch (error) {
                    if (error.status === 400) {
                        setMessage(error.error.ErrorMessage)
                    } else {
                        onError(error)
                    }
                } finally {
                    loader(false)
                }
            })()
        }

        const handleMessage = async (event) => {
            if (event.data?.type === "CreditGuardResponse") {
                loader(true)
                const response = event.data.response
                
                try {
                    const tokenResult = await saveToken(response, { entities, folioId })                   
                    if (onTokenAdded) {
                        onTokenAdded(tokenResult)
                    }
                } catch (error) {
                    onError(error)
                }
                
                loader(false)
            }
        }

        window.addEventListener("message", handleMessage)
        return () => window.removeEventListener("message", handleMessage)
    }, [HotelId, guestId, entities, folioId, onTokenAdded]);

    useImperativeHandle(ref, () => ({
        saveAction: (parameters) => {
            if (providerType === 'BRIDGEPAY' && iframeRef.current) {
                return new Promise((resolve, reject) => {
                    const handler = async event => {
                        if (event.data && event.data.type === "iframeSaveToken") {
                            window.removeEventListener("message", handler);
                            
                            if (event.data.status === 'success') {
                                try {
                                    const tokenResult = await saveIframeToken(event.data.result, parameters);
                                    resolve(tokenResult);
                                } catch (error) {
                                    reject(error);
                                }
                            } else if (event.data.status === 'fail' || event.data.status === 'validation') {
                                reject({ message: `Error code: ${event.data.errorCode}, Error: ${event.data.error}` });
                            }
                        }
                    };
                    
                    window.addEventListener("message", handler);
                    loader(true);
                    iframeRef.current.contentWindow.postMessage("PerformSaveAction", "*");
                });
            }
            
            // CreditGuard - returns null so performSaveAction uses selectedToken
            // Token already saved through popup, now just need to perform payment
            return Promise.resolve(null);
        }
    }), [providerType, entities, folioId, saveIframeToken, loader]);

    const openPaymentPopup = () => {
        if (iframeSrc) {
            
            let width = 500;
            let height = 500;
            let left = (window.screen.width - width) / 2;
            let top = (window.screen.height - height) / 2;
            const popup = window.open(
                iframeSrc,
                'CreateTokenPayment',
                `width=${width},height=${height},left=${left},top=${top}`
            )
            
            if (!popup) {
                onError({ message: "Popup blocked! Please try again." })
            }
        }
    }

    if (message) {
        return <Message warning visible content={message} />
    } else if (iframeSrc) {
        // CreditGuard = Popup Button
        if (providerType === 'CREDITGUARD') {
            return (
                <Button 
                    primary
                    icon='credit card'
                    content={text("Add")}
                    onClick={openPaymentPopup}
                />
            )
        }
        
        // BridgePay = Embedded iframe with ref
        return <iframe ref={iframeRef} src={iframeSrc} style={{ minHeight: 210 }} />
        
    } else {
        return <Loader active />
    }
});

CreateTokenDialog.displayName = 'CreateTokenDialog';
CreateTokenDialog.propTypes = {
    HotelId: PropTypes.number,
    entities: PropTypes.array,
    folioId: PropTypes.number,
    guestId: PropTypes.number,
    ccProvider: PropTypes.number,
    onTokenAdded: PropTypes.func,
}